import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    effect,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    QueryList,
    signal,
    ViewChild,
    viewChild,
    ViewChildren,
} from '@angular/core';
import { ButtonName } from '@app/components/booking-flow/booking-flow.component';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { TimeOfDay } from '@app/enums/timeOfDay.enum';
import { AccountBookingAdvanceTimeNoticeTypes } from '@app/models/accountBookingAdvanceTimeNoticeTypesEnum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Availability } from '@app/models/availability.model';
import { DateTimeOfDay } from '@app/models/dateTimeOfDay.model';
import { Employee } from '@app/models/employee.model';
import { Service } from '@app/models/service.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { AvailabilitiesService } from '@app/services/availabilities.service';
import { EmployeeService } from '@app/services/employee.service';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import {
    APPOINTMENT_REQUEST,
    LocalStorageService,
    OLD_APPOINTMENT_REQUEST,
    SITE_BOOKING_SETTINGS,
} from '@app/services/local-storage.service';
import { LocaleService } from '@app/services/locale.service';
import { ServiceService } from '@app/services/service.service';
import { convertTo12Hour, convertTo24Hour, dateToStringNoTimeZone } from '@app/shared/helpers/extensions';
import { provideIcons } from '@ng-icons/core';
import { lucideCalendarDays, lucideCalendarX, lucideChevronsRight, lucideTrash2 } from '@ng-icons/lucide';
import { BrnSheet } from '@spartan-ng/brain/sheet';
import { forkJoin, from, map, Observable, of, Subject, Subscription, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IBookingStepComponent } from '../IBookingStepComponent';
import { SelectDateTimeService } from '../select-date-time/select-date-time.service';
import { AvailabilitiesCall } from './models/availabilitiesCall.model';
import { DayInWeek } from './models/dayInWeek.model';
import { Month } from './models/month.model';
import { MonthAvailability } from './models/monthAvailability.model';
import { Week } from './models/week.model';

export enum HorizontalScrollDirection {
    Left,
    Right,
}

@Component({
    selector: 'app-select-date-time',
    templateUrl: './select-date-time.component.html',
    styleUrls: ['./select-date-time.component.css'],
    providers: [SelectDateTimeService, provideIcons({ lucideCalendarDays, lucideCalendarX, lucideChevronsRight, lucideTrash2 })],
    standalone: false,
})
export class SelectDateTimeComponent implements OnInit, OnDestroy, AfterViewChecked, IBookingStepComponent {
    /**
     * Scrollable Date Container
     */
    @ViewChild('datesScroller') datesScroller!: ElementRef<HTMLDivElement>;

    /**
     * All displayed date buttons
     */
    @ViewChildren('dateBtn') dateButtons!: QueryList<ElementRef<HTMLButtonElement>>;

    /**
     * The current selected date on the screen.
     */
    public selected = new Date();
    /**
     * The date to display in the calendar (stays on current month even if no availability)
     */
    public calendarDisplayDate = new Date();

    /**
     * Track the previous month and year to detect when calendar navigation occurs
     */
    private previousCalendarMonth: number = new Date().getMonth();
    private previousCalendarYear: number = new Date().getFullYear();

    /**
     * Track the currently displayed month and year in the calendar view
     */
    public currentViewMonth: number = new Date().getMonth();
    public currentViewYear: number = new Date().getFullYear();

    /**
     * An array that holds the data for the 7 day display on the screen (week list).
     */
    public dateList: DayInWeek[] = [];

    /**
     * An array that holds the data for the max day display on the screen (week list).
     */
    public displayDateList: DayInWeek[] = [];

    /**
     * An array that holds the morning time slots (AM times).
     */
    public morningTimeSlots: Date[] = [];
    /**
     * An array that holds the afternoon time slots (PM times).
     */
    public afternoonTimeSlots: Date[] = [];
    /**
     * An array that holds the evening time slots (after 5 PM).
     */
    public eveningTimeSlots: Date[] = [];
    /**
     * The minimum calendar date that is enabled.
     */
    public minDate: Date | undefined = undefined;
    /**
     * The maximum calendar date that is enabled
     */
    public maxDate: Date | undefined = undefined;
    /**
     * An array that holds availabilities fetched from the API by month and year.
     */
    public monthAvailabilities: MonthAvailability[] = [];
    public isCalendarLoading = false;
    public isForwardButtonDisabled: boolean = false;
    public isBackButtonDisabled: boolean = false;
    public shouldDisableRightChevron: boolean = true;
    public shouldDisableRightChevronMaxAdvanceBook: boolean = false;
    public shouldDisableLeftChevron: boolean = false;
    public horizontalScrollDirection: HorizontalScrollDirection | undefined;
    private _destroyed$: Subject<void>;
    private subscriptions: Subscription[] = [];
    private employeeIdList: string[] = [];
    public isLoading = true;
    public selectedServices: Service[] = [];
    public selectedEmployeeInSheet!: Employee;
    public employees: Employee[] = [];
    private allEmployeesObject: Employee = {
        id: '0',
        name: 'All Employees',
        employeeServicePriceList: [],
    };
    public employeeSelectionSheet = viewChild<BrnSheet>('employeeSelection');
    /**
     * An array that holds a list of models that represent calls to the API that are initiated and still in progress.
     */
    private availabilitiesCalls: AvailabilitiesCall[] = [];
    public isScheduleForEmployeeFound: boolean = true;
    public windowWidth!: number;

    /**
     * Cached visible count to prevent recalculation on every getter call
     */
    private cachedVisibleCount: number = 7;

    /**
     * State of the calendar popover (opens after availabilities are loaded)
     */
    public calendarPopoverState = signal<'open' | 'closed'>('closed');

    /**
     * Whether the current month has no availability
     */
    public hasNoAvailability = false;

    /**
     * The next available date (for no availability message)
     */
    public nextAvailableDate: Date | null = null;

    /**
     * Track the previously selected date before opening calendar (for close button restoration)
     */
    private previousSelectedDate: Date | null = null;

    /**
     * Visible date list for multi-day view (calculated based on available width)
     */
    public get visibleDateList(): DayInWeek[] {
        return this.calculateVisibleDates();
    }

    /**
     * Gets the selected employee from the appointment request
     */
    public get selectedEmployee(): Employee | undefined {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        return appointmentRequest?.employee && appointmentRequest.employee.length > 0 ? appointmentRequest.employee[0] : undefined;
    }

    /**
     * Determines if the Close button should be visible
     * Visible only when reopening after a date has been selected
     */
    public get isCloseButtonVisible(): boolean {
        return this.previousSelectedDate !== null;
    }

    /**
     * Determines if the time of day section should be shown (request-only mode)
     */
    public get canShowTimeOfDay(): boolean {
        return this.isRequestOnlyFeatureEnabled && this.requestAppointmentOnly && !this.isDateWeekLoading;
    }

    /**
     * Checks whether the selected date has availability
     */
    public get isSelectedDateAvailable(): boolean {
        const compareToSelected = (day: DayInWeek) => this.selectDateTimeService.compareDateWithoutTime(day.date, this.selected) === 0;
        const selectedDay = this.dateList.find(compareToSelected) ?? this.displayDateList.find(compareToSelected);
        if (selectedDay) {
            return !!selectedDay.hasAvailabilities;
        }

        // Selected date may be outside both the 7-day dateList and the displayed window (e.g. selected via the calendar),
        // so fall back to checking the underlying monthAvailabilities directly.
        const dateString = this.selectDateTimeService.getDateString(this.selected).split('T')[0];
        const monthData = this.monthAvailabilities.find(
            (m) => m.month === this.selected.getMonth() && m.year === this.selected.getFullYear()
        );
        return !!monthData?.availabilities.some((a) => a.start.includes(dateString));
    }

    /**
     * Determines if the time slot section should be shown
     */
    public get canShowTimeSlot(): boolean {
        return !(this.isRequestOnlyFeatureEnabled && this.requestAppointmentOnly) && !this.isDateWeekLoading;
    }

    /**
     * Ensures the correct First Available employee is saved to local storage when the time slot changes
     */
    private isEmployeeSavedInLSForFirstAvailable = false;

    /**
     * Maximum days allowed for booking, By default 60 days
     */
    private maxDays = 60;

    /**
     * The first day to display in the week view (today)
     */
    public firstDay = new Date(new Date().setHours(0, 0, 0, 0));

    /**
     * The date to display Month and Year
     */
    public displayDate = new Date();

    /**
     * Indicates if the week view is loading more dates
     */
    public isDateWeekLoading = false;

    /**
     * Whether the request-only feature flag is enabled, which hides time slot selection and only allows users to select a preferred time of day
     */
    public isRequestOnlyFeatureEnabled = false;

    /**
     * Whether the account is configured for request-only appointments (no exact time slot picking).
     */
    public requestAppointmentOnly = false;

    /**
     * Saved time-of-day requests for the current appointment when in request-only mode.
     */
    public dateTimeOfDayList: DateTimeOfDay[] = [];

    /**
     * Maximum number of time-of-day entries allowed in request-only mode.
     */
    public readonly maxDateTimeOfDayEntries = 3;

    /**
     * Expose TimeOfDay enum to the template.
     */
    public readonly TimeOfDay = TimeOfDay;

    /**
     * Ordered list of time-of-day options shown as selectable buttons.
     */
    public readonly timeOfDayOptions: TimeOfDay[] = [TimeOfDay.Anytime, TimeOfDay.Morning, TimeOfDay.Afternoon, TimeOfDay.Evening];

    constructor(
        private elementRef: ElementRef,
        private availabilitiesService: AvailabilitiesService,
        private selectDateTimeService: SelectDateTimeService,
        private localStorageService: LocalStorageService,
        private localeService: LocaleService,
        public cd: ChangeDetectorRef,
        private employeeService: EmployeeService,
        private serviceService: ServiceService,
        private featureFlagService: FeatureFlagService
    ) {
        this._destroyed$ = new Subject();

        this.isRequestOnlyFeatureEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.DSBRequestOnly);
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.requestAppointmentOnly = !!siteBookingSettings?.requestAppointmentOnly;
        if (this.isRequestOnlyFeatureEnabled && this.requestAppointmentOnly) {
            this.titleBarTitle = 'Request Time of Day';
        }

        // Watch for popover state changes
        effect(() => {
            const state = this.calendarPopoverState();
            if (state === 'open') {
                // When popover opens, update the availability state for the displayed month
                setTimeout(() => {
                    this.updateNoAvailabilityState();
                    this.cd.detectChanges();
                });
            }
        });
    }

    // Implementation of IBookingStepComponent
    public continueButtonText: string = ButtonName.ReviewDetails;
    public nextStepRoute: string = 'review';
    public previousStepRoute: string = 'service';
    public titleBarTitle: string = 'Select Time';
    public showTitleBarBackButton = true;
    public isValid(): boolean {
        if (this.isRequestOnlyFeatureEnabled && this.requestAppointmentOnly) {
            return this.dateTimeOfDayList.length >= 1;
        }
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const isValidEmployee = appointmentRequest?.employee?.[0]?.firstAvailable ? this.isEmployeeSavedInLSForFirstAvailable : true;
        return !!appointmentRequest?.datetime && isValidEmployee;
    }

    async ngOnInit() {
        this.onResize(null);
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.dateTimeOfDayList = appointmentRequest?.dateTimeOfDayList ?? [];
        this.isEmployeeSavedInLSForFirstAvailable = appointmentRequest?.employee?.[0]?.firstAvailable ?? true;
        if (appointmentRequest?.rescheduleGuid) {
            this.showTitleBarBackButton = false;
            await this.setServiceAndEmployeeToLocalStorage(appointmentRequest);
        }
        this.employeeIdList = this.employeeService.getEmployeesFromServiceList(appointmentRequest.service);
        this.selectedServices = appointmentRequest.service || [];
        this.fetchEmployees(
            this.employeeIdList,
            this.selectedServices.map((s) => s.id)
        );

        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        if (siteBookingSettings?.appointmentMaxAdvanceBooking) {
            this.maxDate = this.adjustDays(new Date(), siteBookingSettings.appointmentMaxAdvanceBooking);
            this.maxDays = siteBookingSettings.appointmentMaxAdvanceBooking;
        }
        this.displayDateList = new Week(this.firstDay, this.maxDays).daysInWeek;

        if (siteBookingSettings?.accountBookingAdvanceTimeNotice && siteBookingSettings?.accountBookingAdvanceTimeNoticeType) {
            this.minDate = this.calculateMinDate(
                siteBookingSettings.accountBookingAdvanceTimeNotice,
                siteBookingSettings.accountBookingAdvanceTimeNoticeType
            );
            this.selected = new Date(this.minDate);
            this.calendarDisplayDate = new Date(this.selected);
        } else {
            this.minDate = new Date();
        }

        this.FetchAvailabilities();

        this.subscriptions.push(
            this.selectDateTimeService.onAvailabilityDataFetched.subscribe(() => {
                this.selectTimeSlotFromLocalStorage();
                // Select first day of the (this.dateList) week list, if the current selected day is not one of the week list
                if (
                    this.dateList.length &&
                    !this.dateList.some((dl) => this.selectDateTimeService.compareDateWithoutTime(dl.date, this.selected) === 0)
                ) {
                    this.cd.detectChanges();
                    this.manipulateDayOrTimeSlot(
                        `#day-slot-${this.dateList[0].date.getDate()}-${this.dateList[0].date.getMonth()}`,
                        'on',
                        'day-slot-active'
                    );
                } else {
                    if (!this.isWeekAvailable()) {
                        this.autoSelectFirstAvailableDate();
                    }
                }
            })
        );
        this.cd.detectChanges();

        this.setupMonthNavigation();
    }

    /**
     * Checks after each view update if the calendar month has changed, if changed trigger updateNoAvailabilityState
     */
    ngAfterViewChecked() {
        if (this.calendarDisplayDate) {
            // Check the calendar header text to detect month changes
            // Use a more specific selector to get the header inside the popover
            const calendarHeader = document.querySelector('hlm-calendar [brnCalendarHeader]');

            if (calendarHeader) {
                const headerText = calendarHeader.textContent?.trim() || '';

                // Only process if we have valid header text
                if (headerText) {
                    // Header format is "Mon Year" (e.g., "Oct 2025")
                    const match = headerText.match(/(\w+)\s+(\d{4})/);
                    if (match) {
                        const monthName = match[1];
                        const year = parseInt(match[2]);
                        const monthMap: Record<string, number> = {
                            Jan: 0,
                            Feb: 1,
                            Mar: 2,
                            Apr: 3,
                            May: 4,
                            Jun: 5,
                            Jul: 6,
                            Aug: 7,
                            Sep: 8,
                            Oct: 9,
                            Nov: 10,
                            Dec: 11,
                        };
                        const displayedMonth = monthMap[monthName];

                        if (
                            displayedMonth !== undefined &&
                            (displayedMonth !== this.previousCalendarMonth || year !== this.previousCalendarYear)
                        ) {
                            this.previousCalendarMonth = displayedMonth;
                            this.previousCalendarYear = year;

                            // Update current view month and year
                            this.currentViewMonth = displayedMonth;
                            this.currentViewYear = year;

                            // If selected date is in the displayed month, highlight it; otherwise, show the month with no highlight
                            if (this.selected.getMonth() === displayedMonth && this.selected.getFullYear() === year) {
                                this.calendarDisplayDate = new Date(this.selected);
                            }

                            // Check if the displayed month's data is loaded, if not fetch it
                            const isMonthLoaded = this.monthAvailabilities.filter((m) => m.month === displayedMonth && m.year === year);
                            if (!isMonthLoaded.length) {
                                // Fetch the month's availabilities
                                const monthDate = new Date(year, displayedMonth, 1);
                                this.fetchFullMonthAvailabilities(monthDate, [], false, true);
                            }

                            // Update the "No Availability" section
                            this.updateNoAvailabilityState();
                            this.cd.detectChanges();
                        }
                    }
                }
            }
        }
    }

    private FetchAvailabilities() {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        if (appointmentRequest.datetime) {
            const dateFromLS = new Date(appointmentRequest.datetime);
            const today = new Date();
            let startDate = today;
            // If the date from local storage is in a future month the whole month should be fetched
            // Else if the date is from the current month but from the future the start date for the fetch
            // availabilities operation needs to be the current day.
            if (
                dateFromLS.getFullYear() > today.getFullYear() ||
                (dateFromLS.getFullYear() === today.getFullYear() && dateFromLS.getMonth() > today.getMonth())
            ) {
                startDate = new Date(dateFromLS.getFullYear(), dateFromLS.getMonth(), 1);
            }
            void this.fetchAvailabilities(startDate, dateFromLS);
        } else {
            void this.fetchAvailabilities();
        }
    }

    /**
     * Handles editing employee selection
     */
    public onEditEmployee(): void {
        this.employeeSelectionSheet()?.open();
    }

    public assignSelectedEmployeeInSheet(selectedEmployeeId: number) {
        this.selectedEmployeeInSheet = this.employees.find((e) => e.id === selectedEmployeeId.toString()) ?? this.allEmployeesObject;
    }

    public onUpdateEmployeeFromSheet() {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        if (this.selectedEmployeeInSheet && this.selectedEmployeeInSheet.id !== appointmentRequest?.employee?.[0]?.id) {
            // Update the appointment request in local storage with the selected employee ID
            this.setEmployeeToLocalStorage();
            // Clear existing availabilities and datetime in localstorage appt and trigger refetching for the selected employee
            this.monthAvailabilities = [];
            this.dateList = [];
            this.displayDateList = new Week(this.firstDay, this.maxDays).daysInWeek;
            this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Datetime);
            this.selected = new Date(this.minDate || new Date());
            this.calendarDisplayDate = new Date(this.selected);
            this.FetchAvailabilities();
        }
        this.employeeSelectionSheet()?.close();
        this.cd.detectChanges();
    }

    private setEmployeeToLocalStorage() {
        const employee = { ...this.selectedEmployeeInSheet };
        if (employee?.id === this.allEmployeesObject.id) {
            Object.assign(employee, { firstAvailable: true, name: 'First Available' });
        }
        const selectedServiceIds = this.selectedServices?.map((s) => s.id);
        if (selectedServiceIds && employee?.employeeServicePriceList) {
            employee.employeeServicePriceList = employee.employeeServicePriceList.filter((e) => selectedServiceIds.includes(e.serviceID));
        }
        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [employee]);
    }

    /**
     * fetch employees by services selected for employee update sheet
     */
    private async fetchEmployees(employeeIdList: string[], serviceIdList: string[]): Promise<void> {
        this.employees = await this.employeeService.getEmployeeList(employeeIdList, serviceIdList);
        this.employees = this.employeeService.sortEmployeeList(this.employees);
        this.allEmployeesObject.employeeServicePriceList =
            this.employeeService.getEmployeeServicePriceListForFirstAvailability(serviceIdList);
        // Only add All Employees (First Available) if there are 2+ actual employees
        if (this.employees.length > 1) {
            this.employees.unshift(this.allEmployeesObject);
        }
    }

    /**
     * Handles "Go to Next Available" button click
     */
    public onGoToNextAvailable(): void {
        if (this.nextAvailableDate) {
            // Update selected date
            this.selected = this.selectDateTimeService.removeTimeFromDate(new Date(this.nextAvailableDate));
            this.calendarDisplayDate = new Date(this.selected);

            // Update the time slots for the new date
            this.splitSelectedDateAvailabilityTimes();

            // Update the day slots in the week view
            this.updateDaySlots(this.selected);

            // Update the week display
            this.updateDaysInWeek(this.dateList, this.monthAvailabilities);

            this.scrollToSelectedDate();

            // Highlight the selected day slot
            this.manipulateDayOrTimeSlot('day-slot-active', 'off', 'day-slot-active');
            this.manipulateDayOrTimeSlot(`#day-slot-${this.selected.getDate()}-${this.selected.getMonth()}`, 'on', 'day-slot-active');

            // Update current view to the month of the next available date
            this.currentViewMonth = this.nextAvailableDate.getMonth();
            this.currentViewYear = this.nextAvailableDate.getFullYear();

            // Update no availability state for the new month
            this.updateNoAvailabilityState();

            // Clear datetime (if exists) in local storage to force re-selection
            this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Datetime);

            this.calendarPopoverState.set('closed');
            this.cd.detectChanges();
        }
    }

    public onCloseCalendar(): void {
        // Restore the previous selected date if it exists
        if (this.previousSelectedDate) {
            this.selected = new Date(this.previousSelectedDate);
            this.calendarDisplayDate = new Date(this.selected);
            this.updateDaySlots(this.selected, true);
        }
        this.calendarPopoverState.set('closed');
    }

    public onOpenCalendar(): void {
        // Store the current selected date before opening calendar
        this.previousSelectedDate = new Date(this.selected);
        this.calendarPopoverState.set('open');
        setTimeout(() => {
            this.updatePopoverWidth();
        });
    }

    /**
     * Calculates the minimum date based on advance time notice and type.
     * @param advanceTimeNotice - The advance time notice value.
     * @param advanceTimeNoticeType - The type of advance time notice (Hours, Days, or None).
     * @returns The calculated minimum date.
     */
    private calculateMinDate(advanceTimeNotice: number, advanceTimeNoticeType: AccountBookingAdvanceTimeNoticeTypes): Date {
        const currentDate = new Date();
        let offsetInMilliseconds = currentDate.getTime();

        if (advanceTimeNoticeType === AccountBookingAdvanceTimeNoticeTypes.Hours) {
            offsetInMilliseconds += advanceTimeNotice * 60 * 60 * 1000;
        } else if (advanceTimeNoticeType === AccountBookingAdvanceTimeNoticeTypes.Days) {
            offsetInMilliseconds += advanceTimeNotice * 24 * 60 * 60 * 1000;
        }

        return new Date(offsetInMilliseconds);
    }

    /**
     * Gets the month and year display for the back button
     */
    public getMonthYearDisplay(): string {
        return this.selected.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }

    /**
     * Calculates how many dates can fit in the container
     * This is called once and cached to keep the visible count constant
     */
    private calculateVisibleCount(): number {
        if (!this.windowWidth) {
            this.windowWidth = this.elementRef.nativeElement.querySelector('#select-date-time-container').offsetWidth;
        }
        const containerWidth = this.windowWidth;

        // Determine date block width and gap based on screen size (matching CSS breakpoints)
        const gap = 12; // gap-3 in Tailwind
        let blockWidth: number;

        if (containerWidth <= 640) {
            // Mobile: 48px width, set in css
            blockWidth = 40;
        } else {
            // other: 80px width
            blockWidth = 80;
        }

        const arrowSpace = 100; // Space for arrows (50px per side to prevent overlap)
        const availableWidth = containerWidth - arrowSpace;
        const maxVisible = Math.floor((availableWidth + gap) / (blockWidth + gap));
        return maxVisible;
    }

    /**
     * Returns visible dates from the dateList
     * Shows a consistent number of dates based on container width
     */
    private calculateVisibleDates(): DayInWeek[] {
        if (this.dateList.length === 0) {
            return [];
        }

        this.cachedVisibleCount = this.calculateVisibleCount();
        return this.dateList.slice(0, Math.min(this.cachedVisibleCount, this.dateList.length));
    }

    public isDateSelected(date: Date): boolean {
        return this.selectDateTimeService.compareDateWithoutTime(date, this.selected) === 0;
    }

    public isToday(date: Date): boolean {
        const today = new Date();
        return this.selectDateTimeService.compareDateWithoutTime(date, today) === 0;
    }

    public isTimeSlotSelected(partOfDay: string, index: number): boolean {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        if (!appointmentRequest?.datetime) {
            return false;
        }

        const selectedDateTime = new Date(appointmentRequest.datetime);

        // Only highlight if on the same date
        if (this.selectDateTimeService.compareDateWithoutTime(this.selected, selectedDateTime) !== 0) {
            return false;
        }

        const timeStringToCompare = this.getTimeInLocaleString(selectedDateTime);
        let foundTimeSlotIndex = -1;
        let foundPartOfDay = '';

        if (partOfDay.includes('morning')) {
            foundTimeSlotIndex = this.morningTimeSlots.findIndex((mts) => this.getTimeInLocaleString(mts) === timeStringToCompare);
            foundPartOfDay = 'ts-morning-';
        } else if (partOfDay.includes('afternoon')) {
            foundTimeSlotIndex = this.afternoonTimeSlots.findIndex((ats) => this.getTimeInLocaleString(ats) === timeStringToCompare);
            foundPartOfDay = 'ts-afternoon-';
        } else if (partOfDay.includes('evening')) {
            foundTimeSlotIndex = this.eveningTimeSlots.findIndex((ets) => this.getTimeInLocaleString(ets) === timeStringToCompare);
            foundPartOfDay = 'ts-evening-';
        }

        return foundPartOfDay === partOfDay && foundTimeSlotIndex === index;
    }

    /**
     * Handles clicking on a date block in the multi-day view
     */
    public onDateBlockClick(date: DayInWeek): void {
        if (this.selectDateTimeService.compareDateWithoutTime(this.selected, date.date) === 0) {
            return;
        }
        // Allow clicking on any date, even without availabilities, to show the view
        this.selected = this.selectDateTimeService.removeTimeFromDate(new Date(date.date));
        this.displayDate = this.selected;
        this.splitSelectedDateAvailabilityTimes();

        // Clear time slot selection when changing date
        this.manipulateDayOrTimeSlot('time-slot-active', 'off', 'time-slot-active');

        // force the user to re-select time when changing date
        this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Datetime);
        this.removeEmployeeFromLSForFirstAvailable();

        this.calendarDisplayDate = new Date(this.selected);
        this.cd.detectChanges();
    }

    /**
     * Gets the next available date from all loaded availabilities
     */
    public getNextAvailableDate(): Date | null {
        const allAvailabilities = this.monthAvailabilities
            .flatMap((m) => m.availabilities)
            .map((a) => new Date(a.start))
            .filter((date) => date > this.selected)
            .sort((a, b) => a.getTime() - b.getTime());

        return allAvailabilities.length > 0 ? allAvailabilities[0] : null;
    }

    /**
     * Navigates to the next available date
     */
    public goToNextAvailable(): void {
        const nextDate = this.getNextAvailableDate();
        if (nextDate) {
            this.selected = this.selectDateTimeService.removeTimeFromDate(nextDate);
            this.updateDaySlots(this.selected);
            this.splitSelectedDateAvailabilityTimes();

            this.manipulateDayOrTimeSlot('time-slot-active', 'off', 'time-slot-active');
            // force the user to re-select time when changing date
            this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Datetime);
            this.removeEmployeeFromLSForFirstAvailable();
            this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
            this.scrollToSelectedDate();
            this.calendarDisplayDate = new Date(this.selected);
            this.cd.detectChanges();
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.windowWidth = this.elementRef.nativeElement.querySelector('#select-date-time-container').offsetWidth;
        this.updatePopoverWidth();
        // Recalculate visible count on resize
        this.cachedVisibleCount = this.calculateVisibleCount();
        this.cd.detectChanges();
    }

    /**
     * Updates the popover width to fit inside the container & viewport
     */
    private updatePopoverWidth(): void {
        setTimeout(() => {
            const container = this.elementRef.nativeElement.querySelector('#select-date-time-container') as HTMLElement;
            const overlayPane = document.querySelector('.cdk-overlay-pane') as HTMLElement;

            if (!container || !overlayPane) {
                return;
            }

            const { left, width } = container.getBoundingClientRect();
            const availableWidth = Math.min(width, window.innerWidth - left);

            Object.assign(overlayPane.style, {
                width: `${availableWidth}px`,
                maxWidth: `${availableWidth}px`,
                left: `${left}px`,
                overflowY: 'auto',
                right: 'auto',
                boxSizing: 'border-box',
            });
        });
    }

    public get HorizontalScrollDirection() {
        return HorizontalScrollDirection;
    }

    /**
     * Checks to see if the date to check should be disabled in the calendar.
     * The function is called for every day in the current month on calendar load.
     * @param date Date to check
     * @returns true if the date should be DISABLED, false if it should be ENABLED
     */
    public filterDates = (date: any): boolean => {
        if (!date) {
            return true; // Disable if no date
        }
        date = this.selectDateTimeService.getDateString(new Date(date.toString()));
        const dateString = date.split('T')[0];

        // Don't disable the currently displayed calendar date (allow it to be shown even if no availability)
        const displayDateString = this.selectDateTimeService.getDateString(this.calendarDisplayDate).split('T')[0];
        if (dateString === displayDateString) {
            return false; // Always enable the currently displayed date
        }

        const month = new Date(date).getMonth();
        const year = new Date(date).getFullYear();
        const monthData = this.monthAvailabilities.find((m) => m.month === month && m.year === year);
        const availabilityFound = monthData?.availabilities.filter((a) => a.start.includes(dateString));

        // Return true to DISABLE the date (no availability found)
        // Return false to ENABLE the date (availability found)
        return !availabilityFound?.length;
    };

    /**
     * Recreates the filterDates function reference to force the calendar to re-evaluate disabled dates.
     * This is necessary because the calendar component only detects changes when the function reference changes.
     */
    private refreshCalendarDateFilter() {
        const originalFilter = this.filterDates;
        this.filterDates = (date: any): boolean => originalFilter.call(this, date);
    }

    /**
     * Checks if the currently selected/displayed month has any availabilities.
     * Updates hasNoAvailability and nextAvailableDate accordingly.
     * Uses currentViewMonth and currentViewYear to determine which month to check.
     */
    private updateNoAvailabilityState() {
        const currentMonth = this.currentViewMonth;
        const currentYear = this.currentViewYear;

        // Check if current month has any availabilities
        const currentMonthData = this.monthAvailabilities.find((m) => m.month === currentMonth && m.year === currentYear);

        const hasAvailabilitiesInCurrentMonth = currentMonthData && currentMonthData.availabilities.length > 0;

        if (hasAvailabilitiesInCurrentMonth) {
            this.hasNoAvailability = false;
            this.nextAvailableDate = null;
        } else {
            // Find the next available date from all months
            const referenceDate = new Date(currentYear, currentMonth, 1);
            const allAvailabilities = this.monthAvailabilities
                .flatMap((m) => m.availabilities)
                .map((a) => new Date(a.start))
                .filter((date) => date > referenceDate)
                .sort((a, b) => a.getTime() - b.getTime());

            if (allAvailabilities.length > 0) {
                this.hasNoAvailability = true;
                this.nextAvailableDate = allAvailabilities[0];
            } else {
                this.hasNoAvailability = false;
                this.nextAvailableDate = null;
            }
        }
    }

    /**
     * Handles the event generated by picking a new date on the calendar popup by updating the picker value and
     * updating the current selected date.
     * @param event - Datepicker date changed event.
     */
    public async onDateChange(event: any) {
        // Prevent unselecting - if event is null/undefined, ignore the change
        // The event can be either the date directly or an object with a value property
        const newDate = event?.value ?? event;
        if (!newDate) {
            // Reset calendarDisplayDate to keep the current selected date
            this.calendarDisplayDate = new Date(this.selected);
            this.calendarPopoverState.set('closed');
            this.cd.detectChanges();
            return;
        }

        this.selected = this.selectDateTimeService.removeTimeFromDate(new Date(newDate));
        this.calendarDisplayDate = new Date(this.selected);

        // force the user to reselect time when changing date
        this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Datetime);
        this.removeEmployeeFromLSForFirstAvailable();

        // If the picked date is close to the end of the month check to see if the next month is loaded and if it's not fetch it from the API
        const isNextMonthLoaded = this.monthAvailabilities.filter((m) => m.month === this.selected.getMonth() + 1);
        if (this.selectDateTimeService.getEndOfMonth(this.selected).getDate() - this.selected.getDate() < 7 && !isNextMonthLoaded.length) {
            this.selectDateTimeService.asyncCallFinishedChevronRight(false);
            this.fetchFullMonthAvailabilities(
                this.selectDateTimeService.getNextMonth(this.selected),
                [],
                false,
                false,
                HorizontalScrollDirection.Right
            );
        }

        // Remove outdated availabilities but exclude months based on current selected date
        let monthToExcudeFromDelete;
        if (this.selected.getDate() >= 15) {
            monthToExcudeFromDelete = this.adjustMonth(this.selected, 1);
        } else {
            monthToExcudeFromDelete = this.adjustMonth(this.selected, -1);
        }
        this.removeOutDatedMonthAvailabilities([new Month(monthToExcudeFromDelete.getFullYear(), monthToExcudeFromDelete.getMonth())]);

        // If the picked date is close to the start of the month check to see if the previous month is loaded and if it's not fetch it from the API
        const isPreviousMonthLoaded = this.monthAvailabilities.filter((m) => m.month === this.selected.getMonth() - 1);
        if (this.selected.getDate() < 7 && !isPreviousMonthLoaded.length) {
            this.selectDateTimeService.asyncCallFinishedChevronLeft(false);
            this.fetchFullMonthAvailabilities(
                this.selectDateTimeService.getPreviousMonth(this.selected),
                [],
                false,
                false,
                HorizontalScrollDirection.Left
            );
        }
        this.updateDaySlots(this.selected);
        this.splitSelectedDateAvailabilityTimes();
        this.manipulateDayOrTimeSlot('time-slot-active', 'off', 'time-slot-active');
        this.manipulateDayOrTimeSlot(`#day-slot-${this.selected.getDate()}-${this.selected.getMonth()}`, 'on', 'day-slot-active');
        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
        this.shouldDisableLeftChevron = this.isLeftChevronDisabled();
        this.shouldDisableRightChevronMaxAdvanceBook = this.shouldDisableRightChevronMaxAdvance();

        this.updateNoAvailabilityState();

        // Close the calendar popover and show the week view
        this.calendarPopoverState.set('closed');

        setTimeout(() => {
            this.scrollToSelectedDate();
        }, 200);
    }

    /**
     * Handles the week scroll event (when the user clicks on the chevrons to move through the 7 day display).
     * @param direction - Direction of the movement.
     */
    public scrollWeekOnClick(direction: HorizontalScrollDirection) {
        if (direction === undefined) {
            return;
        }

        const scrollAmount = this.calculateVisibleCount();

        const firstDateCopy = new Date(this.selected);
        const isWithinCurrentMonth = (date: Date) =>
            date.getMonth() === firstDateCopy.getMonth() && date.getFullYear() === firstDateCopy.getFullYear();
        if (direction === HorizontalScrollDirection.Left) {
            this.scrollWeekLeft(firstDateCopy, isWithinCurrentMonth, scrollAmount);
        } else {
            this.scrollWeekRight(firstDateCopy, isWithinCurrentMonth, scrollAmount);
        }

        // After scrolling, select the first date in the new view
        if (this.dateList.length > 0) {
            const firstVisibleDate = this.dateList[0].date;
            this.selected = new Date(firstVisibleDate);
            this.displayDate = this.selected;
            this.calendarDisplayDate = new Date(this.selected);
            // Update time slots for the newly selected date
            this.splitSelectedDateAvailabilityTimes();

            // Highlight the selected date
            this.manipulateDayOrTimeSlot('day-slot-active', 'off', 'day-slot-active');
            this.manipulateDayOrTimeSlot(`#day-slot-${this.selected.getDate()}-${this.selected.getMonth()}`, 'on', 'day-slot-active');

            // force the user to re-select time when changing date
            this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Datetime);
            this.cd.detectChanges();
        }

        if (direction === HorizontalScrollDirection.Left) {
            this.shouldDisableRightChevronMaxAdvanceBook = false;
            this.shouldDisableLeftChevron = this.isLeftChevronDisabled();
        } else {
            this.shouldDisableLeftChevron = false;
            this.shouldDisableRightChevronMaxAdvanceBook = this.shouldDisableRightChevronMaxAdvance();
        }

        this.scrollToSelectedDate();
    }

    /**
     * Checks if the left navigation chevron for the week list needs to be enabled or disabled.
     */
    public previousWeekEnabled() {
        const tempDateTime = new Date();
        if (!this.dateList[0]?.date) {
            return false;
        }
        if (this.selectDateTimeService.compareDateWithoutTime(this.dateList[0].date, tempDateTime) === 0) {
            return true;
        }
        return false;
    }

    public async onTimeSlotChange(partOfDay: string, id: number) {
        this.manipulateDayOrTimeSlot('time-slot-active', 'off', 'time-slot-active');
        let newTimeParts: string[] = [];
        if (partOfDay.includes('morning')) {
            newTimeParts = convertTo24Hour(this.morningTimeSlots[id].toTimeString(), true).split(':');
        } else if (partOfDay.includes('afternoon')) {
            newTimeParts = convertTo24Hour(this.afternoonTimeSlots[id].toTimeString(), false).split(':');
        } else if (partOfDay.includes('evening')) {
            newTimeParts = convertTo24Hour(this.eveningTimeSlots[id].toTimeString(), false).split(':');
        }
        this.selected.setHours(+newTimeParts[0], +newTimeParts[1]);

        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Datetime, dateToStringNoTimeZone(this.selected));
        await this.saveEmployeeToLSForFirstAvailable();
        this.manipulateDayOrTimeSlot(`#${partOfDay}${id}`, 'on', 'time-slot-active');
    }

    /**
     * Adds a date + time-of-day preference to the request list (request-only mode).
     * Duplicates (same date + same time-of-day) and additions past the max cap are silently ignored.
     */
    public onTimeOfDayClick(timeOfDay: TimeOfDay): void {
        if (this.isMaxDateTimeOfDayReached()) {
            return;
        }

        const dateString = this.toLocalDateString(this.selected);
        if (this.isDateTimeOfDayDuplicate(dateString, timeOfDay)) {
            return;
        }

        this.dateTimeOfDayList = [...this.dateTimeOfDayList, { date: dateString, timeOfDay }];
        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.DateTimeOfDayList, this.dateTimeOfDayList);
        this.cd.detectChanges();
    }

    /**
     * Removes a saved date + time-of-day entry at the given index (request-only mode).
     */
    public onRemoveDateTimeOfDay(index: number): void {
        if (index < 0 || index >= this.dateTimeOfDayList.length) {
            return;
        }
        this.dateTimeOfDayList = this.dateTimeOfDayList.filter((_, i) => i !== index);
        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.DateTimeOfDayList, this.dateTimeOfDayList);
        this.cd.detectChanges();
    }

    public isMaxDateTimeOfDayReached(): boolean {
        return this.dateTimeOfDayList.length >= this.maxDateTimeOfDayEntries;
    }

    public parseDateTimeOfDayDate(dateString: string): Date {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    private isDateTimeOfDayDuplicate(dateString: string, timeOfDay: TimeOfDay): boolean {
        return this.dateTimeOfDayList.some((d) => d.date === dateString && d.timeOfDay === timeOfDay);
    }

    private toLocalDateString(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    public onDaySlotChange($event: any) {
        this.manipulateDayOrTimeSlot('day-slot-active', 'off', 'day-slot-active');
        this.manipulateDayOrTimeSlot('time-slot-active', 'off', 'time-slot-active');

        this.selected = new Date($event.value);
        const monthsInWeekList = this.getMonthsInWeekList();
        const monthToExclude = this.getMonthsToExclude();
        if (monthToExclude) {
            monthsInWeekList.push(monthToExclude);
        }
        this.removeOutDatedMonthAvailabilities(monthsInWeekList);
        this.splitSelectedDateAvailabilityTimes();
        this.manipulateDayOrTimeSlot(
            `#day-slot-${new Date($event.value).getDate()}-${new Date($event.value).getMonth()}`,
            'on',
            'day-slot-active'
        );
        this.calendarDisplayDate = new Date(this.selected);
    }

    /**
     * Matches screen resolution with the media queries.
     * The function is called from the places where we need to show or hide chevrons depending on the design.
     * @returns True - The result is true if the screen matches one of the media queries for tablet or desktop devices.
     * False - The result is false if the screen does not match any of the media queries.
     */
    public isMatchingResolution(): boolean {
        const mediaQueries = [
            'screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: portrait)',
            'screen and (min-width: 1080px) and (max-width: 1080px) and (device-width: 1080px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1024px) and (max-width: 1024px) and (device-width: 1024px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1180px) and (max-width: 1180px) and (device-width: 1180px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1194px) and (max-width: 1194px) and (device-width: 1194px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1366px) and (max-width: 1366px) and (device-width: 1366px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-width: 1368px) and (max-width: 1368px) and (device-width: 1368px) and (orientation: landscape) and (min-resolution: 1dppx)',
            'screen and (min-device-width: 602px) and (orientation: landscape) and (min-resolution: 1.331dppx) and (max-resolution: 1.332dppx)',
            'screen and (min-device-width: 602px) and (orientation: landscape) and (min-resolution: 2dppx) and (device-aspect-ratio: 40 / 23)',
            'screen and (min-width: 1280px) and (device-width: 1280px) and (max-width: 1280px) and (orientation: landscape) and (min-resolution: 1dppx) and (device-aspect-ratio: 8 / 5)',
            'screen and (min-width: 1024px)',
        ];

        return mediaQueries.some((query) => window.matchMedia(query).matches);
    }

    /**
     * Checks to see if the week has availabilities
     * @returns True if some days have availabilities
     */
    public isWeekAvailable() {
        return this.dateList.some((dl) => dl.hasAvailabilities);
    }

    /**
     * Saves the auto-chosen employee to local storage if the First Available option was used
     */
    private async saveEmployeeToLSForFirstAvailable() {
        this.isEmployeeSavedInLSForFirstAvailable = false;
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

        // Only fetch employee list if "First Available" option is selected
        if (appointmentRequest.datetime && appointmentRequest.employee?.[0]?.firstAvailable) {
            // Find the availability that was picked (the specific time slot).
            const pickedAvailability = this.monthAvailabilities
                .find((ma) => ma.availabilities.find((a) => a.start === appointmentRequest.datetime.toString()))
                ?.availabilities.find((a) => a.start === appointmentRequest.datetime.toString());
            const employeeIDList = this.employeeService.getEmployeesFromServiceList(appointmentRequest.service);
            const serviceIDList = appointmentRequest.service.map((service) => service.id);
            from(this.employeeService.getEmployeeList(employeeIDList, serviceIDList))
                .pipe(takeUntil(this._destroyed$))
                .subscribe((employeeList) => {
                    // Filter the list of all available employees by the employeeID from the picked availability
                    const employeeToSave = employeeList.filter((e) => e.id === pickedAvailability?.employeeID.toString());
                    if (appointmentRequest.employee[0].firstAvailable) {
                        employeeToSave[0].firstAvailable = true;
                    }
                    if (employeeList?.length && employeeToSave) {
                        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, employeeToSave);
                    }
                    this.isEmployeeSavedInLSForFirstAvailable = true;
                });
        }
    }

    /**
     * Remove an employee from local storage if the chosen option is 'First Available'
     * @param appointmentRequest The appointment request from local storage
     */
    private removeEmployeeFromLSForFirstAvailable(appointmentRequest?: AppointmentRequest) {
        let apptRequest;
        if (!appointmentRequest) {
            apptRequest = appointmentRequest;
        } else {
            apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        }

        if (apptRequest?.employee.length && apptRequest?.employee[0].firstAvailable && apptRequest.employee[0].id !== '0') {
            const serviceIdList = apptRequest.service.map((service) => service.id);
            const employeeServicePriceList = this.employeeService.getEmployeeServicePriceListForFirstAvailability(serviceIdList);
            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [
                {
                    id: '0',
                    name: 'First Available',
                    employeeServicePriceList: employeeServicePriceList,
                    firstAvailable: true,
                },
            ]);
        }
    }

    /**
     * Get the month indexes from the current week list
     * @returns A list of month indexes
     */
    private getMonthsInWeekList(): Month[] {
        const months: Month[] = [];
        for (const day of this.dateList) {
            const month = day.date.getMonth();
            if (months.findIndex((m) => m.month === month) === -1) {
                months.push(new Month(day.date.getFullYear(), month));
            }
        }
        return months;
    }

    /**
     * Returns an object containing the year and month to exclude from a removing of outdated month availabilities.
     * The month to exclude is determined based on the day of the first date in the list.
     * If the day is >= 15, the month to exclude is the next month.
     * If the day is < 15, the month to exclude is the previous month.
     * @returns a Month indexes.
     */
    private getMonthsToExclude() {
        let month!: Month;
        const day = this.dateList[0].date.getDate();
        if (day >= 15) {
            month = {
                year: this.adjustMonth(this.dateList[0].date, 1).getFullYear(),
                month: this.adjustMonth(this.dateList[0].date, 1).getMonth(),
            };
        } else if (day < 15) {
            month = {
                year: this.adjustMonth(this.dateList[0].date, -1).getFullYear(),
                month: this.adjustMonth(this.dateList[0].date, -1).getMonth(),
            };
        }

        return month;
    }

    /**
     * Populates the monthAvailabilities list based on the passed in availabilities and by creating or updating MonthAvailability instances.
     * @param availabilities - A list of availabilities that are to be added to the monthAvailabilities list.
     */
    private populateMonthAvailabilities(availabilities: Availability[]) {
        // Skip if no availabilities provided
        if (!availabilities || availabilities.length === 0) {
            return;
        }

        // Break down the input availabilities into months
        const months: Availability[][] = [];

        for (const availability of availabilities) {
            availability.start = dateToStringNoTimeZone(new Date(availability.start));
            availability.end = dateToStringNoTimeZone(new Date(availability.end));
            const month = availability.start.slice(0, 7);
            const index = months.findIndex((list) => list[0]?.start.slice(0, 7) === month);
            if (index === -1) {
                months.push([availability]);
            } else {
                months[index].push(availability);
            }
        }

        // For every broken down month list of availabilities create/update a MonthAvailability object and add it to the monthAvailabilities list
        for (const month of months) {
            // Check if the month exists in the monthAvailabilities list
            const monthIndex = new Date(month[0].start).getMonth();
            const yearIndex = new Date(month[0].start).getFullYear();
            const monthExists = this.monthAvailabilities.findIndex((monthAvailability) => {
                return monthAvailability.month === monthIndex && monthAvailability.year === yearIndex;
            });
            // If the month is not present in the monthAvailabilities list create a new MonthAvailability object
            if (monthExists === -1) {
                this.monthAvailabilities.push(new MonthAvailability(month));
                // If the month exists in the monthAvailabilities list update the existing MonthAvailability object
            } else {
                this.monthAvailabilities.find((m) => m.month === monthIndex && m.year === yearIndex)?.updateMonthAvailability(month);
            }
        }

        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);

        // Refresh the calendar's date filter to re-evaluate which dates should be enabled/disabled
        this.refreshCalendarDateFilter();
        this.cd.detectChanges();
    }

    /**
     * Fetches the availabilities from the API.
     * The fetching cosists of two calls. The first call will be awaited and will fetch 7 days starting from fetchStartDate.
     * The second call will fetch the rest of that month. The second call is not awaited.
     * @param initialFetchStartDate - The date from which the first 7 days of availabilities will be loaded.
     */
    private async fetchAvailabilities(initialFetchStartDate?: Date, selectedDate?: Date) {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

        const startDate = initialFetchStartDate ?? new Date();
        // Increase the startDate by 6 days so that we have our initial 7 day fetch range
        let endDate = new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000);
        if (selectedDate) {
            const tempEndDate = new Date(selectedDate.getTime() + 6 * 24 * 60 * 60 * 1000);
            // if the selectedDate was 6 or more days in the past, the endDate will still be before the startDate even
            // after the 6 day adjustment.  This can happen if there is an old appointment request in local storage.
            // only use the new end date if it's in the future
            if (tempEndDate >= startDate) {
                endDate = tempEndDate;
            }
        }

        const availabilities$: Observable<Availability[]>[] = [];
        // If the the chosen employee is 'First Available' make a call for each employee
        if (appointmentRequest?.employee[0]?.firstAvailable && this.employeeIdList.length) {
            this.isLoading = true;
            this.fetchAvailabilitiesForAllEmployees(availabilities$, this.employeeIdList, startDate, endDate);
        } else {
            availabilities$.push(
                this.getAvailabilitiesInRange(startDate, endDate, undefined, undefined, undefined, appointmentRequest.rescheduleGuid)
            );
        }
        // Once the availabilities are fetched duplicates need to be removet (First Available case)
        forkJoin(availabilities$)
            // The pipe will flatten the returned array since it is an array of arrays if the 'First Available' option was choosen
            .pipe(
                map((availabilities) => availabilities.reduce((acc, cur) => acc.concat(cur), [])),
                catchError((error) => {
                    if (error.message.includes('No schedules found in the database for the following employeeID')) {
                        this.isScheduleForEmployeeFound = false;
                    }
                    return of([]);
                })
            )
            .subscribe((availabilities) => {
                this.handleInitialSevenDaysAvailabilities(availabilities, selectedDate, startDate, appointmentRequest);
                this.loadRestOfMonth(endDate, appointmentRequest);
                this.loadMonthBorders(appointmentRequest, selectedDate, startDate);
            });
    }

    /**
     * A helper function that does some additional setup with the availabilities that are returned from the API for the first seven days.
     * @param availabilities The availabilities that are returned from the API.
     * @param selectedDate The current selected date.
     * @param startDate The start date for the fetch availabilities API call.
     * @param appointmentRequest The appointment request from local storage.
     */
    private handleInitialSevenDaysAvailabilities(
        availabilities: Availability[],
        selectedDate: Date | undefined,
        startDate: Date,
        appointmentRequest: AppointmentRequest
    ) {
        availabilities = this.removeDuplicateAvailabilities(availabilities);

        if (availabilities) {
            this.populateMonthAvailabilities(availabilities);
        }

        if (selectedDate) {
            this.updateDaySlots(selectedDate);
        } else {
            this.updateDaySlots(startDate);
        }

        // If the chosen appointmentRequest.datetime exists in local storage use that as selected date time,
        // Else set the selected date as today's date
        if (appointmentRequest.datetime) {
            this.selected = new Date(appointmentRequest.datetime);
            this.calendarDisplayDate = new Date(this.selected);
        }
        this.splitSelectedDateAvailabilityTimes();
        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
        if (appointmentRequest.datetime) {
            this.preselectTimeSlot();
        }
        // Select day slot based on this.selected
        this.manipulateDayOrTimeSlot(`#day-slot-${this.selected.getDate()}-${this.selected.getMonth()}`, 'on', 'day-slot-active');
        this.selectDateTimeService.availabilityDataFetched();
        this.shouldDisableRightChevronMaxAdvanceBook = this.shouldDisableRightChevronMaxAdvance();
        this.isLoading = false;

        // Update no availability state for initial load
        this.updateNoAvailabilityState();

        // If there's a datetime in local storage, show week view with selection
        // Otherwise, open the calendar popover to let user pick a date
        if (appointmentRequest.datetime) {
            // Keep calendar closed - show week view with selected date/time
            this.calendarPopoverState.set('closed');
        } else {
            // No datetime saved - open calendar popover for user to select
            this.calendarPopoverState.set('open');
            // Update popover width and position after it opens (with delay to ensure DOM is updated)
            setTimeout(() => {
                this.updatePopoverWidth();
            }, 0);
        }

        this.cd.detectChanges();
    }

    /**
     * A helper for the fetch availabilities function. Loads the rest of the month that comes after the initial 7 day load of fetchAvailabilities.
     * @param endDate The end date for the fetch availabilities API call.
     * @param appointmentRequest The appointment request from local storage.
     */
    private loadRestOfMonth(endDate: Date, appointmentRequest: AppointmentRequest) {
        let nextCallStartDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
        let nextCallEndDate = this.selectDateTimeService.getEndOfMonth(nextCallStartDate);
        nextCallStartDate = this.selectDateTimeService.removeTimeFromDate(new Date(nextCallStartDate));
        if (nextCallStartDate.getDate() > 28) {
            nextCallEndDate = this.selectDateTimeService.getEndOfMonth(this.adjustMonth(nextCallStartDate, 1));
        }
        if (appointmentRequest?.employee[0].firstAvailable && this.employeeIdList.length) {
            const availabilities$: Observable<Availability[]>[] = [];
            this.fetchAvailabilitiesForAllEmployees(availabilities$, this.employeeIdList, nextCallStartDate, nextCallEndDate);
            forkJoin(availabilities$)
                .pipe(
                    map((availabilities) => availabilities.reduce((acc, cur) => acc.concat(cur), [])),
                    catchError((error) => {
                        if (error.message.includes('No schedules found in the database for the following employeeID')) {
                            this.isScheduleForEmployeeFound = false;
                        }
                        return of([]);
                    })
                )
                .subscribe((availabilities) => {
                    this.handleAPIAvailabilitiesResponse(availabilities);
                    if (!this.isScheduleForEmployeeFound) {
                        this.isLoading = false;
                    }
                });
        } else {
            void this.getAvailabilitiesInRangeAsync(
                nextCallStartDate,
                nextCallEndDate,
                HorizontalScrollDirection.Right,
                undefined,
                undefined,
                undefined,
                undefined,
                appointmentRequest.rescheduleGuid
            );
        }
    }

    /**
     * A helper for the fetchAvailabilities function. Loads the availabilities for the case where days from one month are only partially needed.
     * @param appointmentRequest The appointment request from local storage.
     * @param selectedDate The current selected date.
     * @param startDate The start date for the fetch availabilities API call.
     */
    private loadMonthBorders(appointmentRequest: AppointmentRequest, selectedDate: Date | undefined, startDate: Date) {
        const today = new Date();
        if (
            selectedDate &&
            selectedDate.getDate() < 15 &&
            (selectedDate.getFullYear() > startDate.getFullYear() ||
                (selectedDate.getFullYear() === startDate.getFullYear() && selectedDate.getMonth() > today.getMonth()))
        ) {
            let onePreviousMonthStartDate = this.selectDateTimeService.getPreviousMonth(selectedDate);
            if (
                onePreviousMonthStartDate.getFullYear() === today.getFullYear() &&
                onePreviousMonthStartDate.getMonth() === today.getMonth()
            ) {
                onePreviousMonthStartDate = today;
            }
            const onePreviousMonthEndDate = this.selectDateTimeService.getEndOfMonth(onePreviousMonthStartDate);
            const availabilities$: Observable<Availability[]>[] = [];
            if (appointmentRequest?.employee[0].firstAvailable && this.employeeIdList.length) {
                this.fetchAvailabilitiesForAllEmployees(
                    availabilities$,
                    this.employeeIdList,
                    onePreviousMonthStartDate,
                    onePreviousMonthEndDate
                );
            } else {
                void this.getAvailabilitiesInRangeAsync(
                    onePreviousMonthStartDate,
                    onePreviousMonthEndDate,
                    HorizontalScrollDirection.Left,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    appointmentRequest.rescheduleGuid
                );
            }
            forkJoin(availabilities$)
                .pipe(map((availabilities) => availabilities.reduce((acc, cur) => acc.concat(cur), [])))
                .subscribe((availabilities) => {
                    this.handleAPIAvailabilitiesResponse(availabilities);
                });
        }
    }

    /**
     * A helper function that does some additional setup with the availabilities that are returned from the API.
     * @param availabilities The availabilities that are returned from the API.
     */
    private handleAPIAvailabilitiesResponse(availabilities: Availability[]) {
        availabilities = this.removeDuplicateAvailabilities(availabilities);
        this.populateMonthAvailabilities(availabilities);
        this.selectDateTimeService.asyncCallFinishedChevronRight(true);
        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
        this.selectDateTimeService.availabilityDataFetched();
        this.splitSelectedDateAvailabilityTimes();
        this.scrollToSelectedDate();
        this.cd.detectChanges();
    }

    /**
     * Removes duplicate availabilities from an array of Availability objects by randomly choosing
     * one duplicate from each group of duplicates with the same start and end times.
     * @param availabilities - An array of Availability objects that may contain duplicates.
     * @returns An array of Availability objects with duplicates removed.
     */
    private removeDuplicateAvailabilities(availabilities: Availability[]) {
        // Group duplicates by start and end times as the key
        const groupedDuplicates: { [key: string]: Availability[] } = {};

        availabilities.forEach((item) => {
            const startDate = this.selectDateTimeService.removeSecondsFromDate(new Date(item.start));
            const key = `${startDate}`;
            if (!groupedDuplicates[key]) {
                groupedDuplicates[key] = [];
            }
            groupedDuplicates[key].push(item);
        });

        // Randomly choose one duplicate from each group and flatten the groups
        const filteredAvailabilities: Availability[] = [];

        for (const key in groupedDuplicates) {
            if (groupedDuplicates.hasOwnProperty(key)) {
                const duplicatesGroup = groupedDuplicates[key];
                filteredAvailabilities.push(duplicatesGroup[this.randomIndex(duplicatesGroup)]);
            }
        }

        return filteredAvailabilities;
    }

    /**
     * Generates a random index for a given array.
     *
     * @param arr - The array for which a random index is generated.
     * @returns A random index within the bounds of the input array.
     */
    private randomIndex(arr: Availability[]): number {
        return Math.floor(Math.random() * arr.length);
    }

    /**
     * Fetches availabilities for all employees that are registered in the chosen service in local storage.
     * This is used with the 'First Available' option
     * @param availabilities$ A reference to an arrray that needs to be populated with the results from the API calls.
     * @param appointmentRequest The appoitment request from local storage.
     * @param startDate The start date of the API getAvailabilities call.
     * @param endDate The end date of the API getAvailabilities call.
     * @param showCalendarSpinner An indicator if the popup calendar spinner should be shown.
     */
    private fetchAvailabilitiesForAllEmployees(
        availabilities$: Observable<Availability[]>[],
        employeeIDList: string[],
        startDate: Date,
        endDate: Date,
        showCalendarSpinner: boolean = false
    ) {
        for (const employeeID of employeeIDList) {
            availabilities$.push(this.getAvailabilitiesInRange(startDate, endDate, false, showCalendarSpinner, employeeID));
        }
    }

    /**
     * Fetches the availabilities from the API for one month.
     * This function will also remove outdated availabilities by month from memory.
     * @param firstDateOfTheMonth - The start date for the fetch operation (first day of the month).
     * @param additionalMonthIndexesForExludeFromRemove - Month indexes of the months that are not to be removed from memory, besides
     * the current selected month (that is always in memory).
     */
    private fetchFullMonthAvailabilities(
        firstDateOfTheMonth: Date,
        additionalMonthIndexesForExludeFromRemove: Month[],
        showSpinner: boolean = false,
        showCalendarSpinner: boolean = false,
        direction?: HorizontalScrollDirection
    ) {
        const tempDate = new Date();
        if (this.selectDateTimeService.compareDateWithoutTime(firstDateOfTheMonth, tempDate) === -1) {
            firstDateOfTheMonth = tempDate;
        }
        const startDate = new Date(firstDateOfTheMonth);
        const endDate = this.selectDateTimeService.getEndOfMonth(firstDateOfTheMonth);
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

        if (appointmentRequest?.employee[0].firstAvailable && this.employeeIdList.length) {
            const availabilities$: Observable<Availability[]>[] = [];
            const availabilitiesCall = new AvailabilitiesCall(startDate, endDate);
            this.availabilitiesCalls.push(availabilitiesCall);
            this.fetchAvailabilitiesForAllEmployees(availabilities$, this.employeeIdList, startDate, endDate, showCalendarSpinner);
            forkJoin(availabilities$)
                .pipe(map((availabilities) => availabilities.reduce((acc, cur) => acc.concat(cur), [])))
                .subscribe((availabilities) => {
                    availabilities = this.removeDuplicateAvailabilities(availabilities);
                    this.removeOutDatedMonthAvailabilities([
                        new Month(startDate.getFullYear(), startDate.getMonth()),
                        ...additionalMonthIndexesForExludeFromRemove,
                    ]);
                    this.populateMonthAvailabilities(availabilities);
                    this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
                    if (direction === HorizontalScrollDirection.Right) {
                        this.selectDateTimeService.asyncCallFinishedChevronRight(true);
                    } else if (direction === HorizontalScrollDirection.Left) {
                        this.selectDateTimeService.asyncCallFinishedChevronLeft(true);
                    }
                    this.selectDateTimeService.calendarDataChanged();
                    this.removeFromAvailabilitiesCalls(availabilitiesCall);
                    this.isDateWeekLoading = false;
                });
        } else {
            const availabilitiesCall = new AvailabilitiesCall(startDate, endDate);
            this.availabilitiesCalls.push(availabilitiesCall);
            void this.getAvailabilitiesInRangeAsync(
                startDate,
                endDate,
                direction,
                showSpinner,
                showCalendarSpinner,
                undefined,
                availabilitiesCall,
                appointmentRequest.rescheduleGuid
            );
        }
    }

    /**
     * Removes an availability call from the availabilitiesCalls.
     * @param availabilitiesCallToRemove Availibility call to remove.
     */
    private removeFromAvailabilitiesCalls(availabilitiesCallToRemove: AvailabilitiesCall) {
        const indexOfItemToRemove = this.availabilitiesCalls.indexOf(availabilitiesCallToRemove);
        if (indexOfItemToRemove > -1) {
            this.availabilitiesCalls.splice(indexOfItemToRemove, 1);
        }
    }

    /**
     * Checks to ses if specific call with startDate and endDate is in progress.
     * @returns True - if a call currently is in progress;
     *          False - if a call currently is not in progress.
     */
    private callToAvailabilitiesInitiated(startDate: Date, endDate: Date) {
        return this.availabilitiesCalls.find(
            (c) => c.startDate.getTime() === startDate.getTime() && c.endDate.getTime() === endDate.getTime()
        );
    }

    /**
     * Calls the service in order to fetch availabilities for a given date range.
     * This call is not awaited.
     * @param startDate Start date of the availabilities fetch range.
     * @param endDate End date of the availabilities fetch range.
     * @param showSpinner An indicator of whether the spinner should be displayed while loading the data from the API.
     */
    private async getAvailabilitiesInRangeAsync(
        startDate: Date,
        endDate: Date,
        direction?: HorizontalScrollDirection,
        showSpinner?: boolean,
        showCalendarSpinner?: boolean,
        employeeID?: string,
        availabilitiesCall?: AvailabilitiesCall,
        guidToIgnore?: string
    ) {
        if (direction === HorizontalScrollDirection.Right) {
            this.selectDateTimeService.asyncCallFinishedChevronRight(false);
        } else if (direction === HorizontalScrollDirection.Left) {
            this.selectDateTimeService.asyncCallFinishedChevronLeft(false);
        }
        return this.availabilitiesService
            .getAvailabilities(
                this.selectDateTimeService.getDateString(startDate),
                this.selectDateTimeService.getDateString(endDate),
                availabilitiesCall ? false : showSpinner,
                showCalendarSpinner,
                employeeID,
                undefined,
                guidToIgnore
            )
            .pipe(
                takeUntil(this._destroyed$),
                catchError((error) => {
                    if (error.message.includes('No schedules found in the database for the following employeeID')) {
                        this.isScheduleForEmployeeFound = false;
                    }
                    return of([]);
                })
            )
            .subscribe((availabilities) => {
                if (availabilities) {
                    this.populateMonthAvailabilities(availabilities);
                    if (availabilitiesCall) {
                        this.removeFromAvailabilitiesCalls(availabilitiesCall);
                        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
                    }
                    // Update no availability state for current month
                    this.updateNoAvailabilityState();
                    this.cd.detectChanges();
                }
                this.selectDateTimeService.asyncCallFinishedChevronRight(true);
                this.selectDateTimeService.asyncCallFinishedChevronLeft(true);

                this.isDateWeekLoading = false;
            });
    }

    /**
     * Calls the service in order to fetch availabilities for a given date range.
     * This call is awaited.
     * @param startDate Start date of the availabilities fetch range.
     * @param endDate End date of the availabilities fetch range.
     * @param showSpinner An indicator of whether the spinner should be displayed while loading the data from the API.
     */
    private getAvailabilitiesInRange(
        startDate: Date,
        endDate: Date,
        showSpinner?: boolean,
        showCalendarSpinner?: boolean,
        employeeID?: string,
        guidToIgnore?: string
    ): Observable<Availability[]> {
        return this.availabilitiesService
            .getAvailabilities(
                this.selectDateTimeService.getDateString(startDate),
                this.selectDateTimeService.getDateString(endDate),
                showSpinner,
                showCalendarSpinner,
                employeeID,
                undefined,
                guidToIgnore
            )
            .pipe(takeUntil(this._destroyed$));
    }

    /**
     * Splits the availabilities for a certain day into AM and PM times and adds them to their respective lists.
     */
    private splitSelectedDateAvailabilityTimes() {
        this.morningTimeSlots = [];
        this.afternoonTimeSlots = [];
        this.eveningTimeSlots = [];

        const selectedDateString = this.selectDateTimeService.getDateString(this.selected).split('T')[0];

        // Select all availabilities for the current selected date
        const timesInDay = this.monthAvailabilities
            .find((m) => m.month === this.selected.getMonth())
            ?.availabilities.filter((a) => a.start.includes(selectedDateString));

        if (!timesInDay) {
            return;
        }

        timesInDay.sort((a1, a2) => new Date(a1.start).getTime() - new Date(a2.start).getTime());

        // Splits the selected availabilities into morning, afternoon, and evening times
        timesInDay.forEach((availability) => {
            const time = availability.start.slice(availability.start.indexOf('T') + 1);
            const dateObj = new Date(`1970-01-01T${time}`);
            const hours = dateObj.getHours();

            if (hours < 12) {
                this.morningTimeSlots.push(dateObj);
            } else if (hours < 17) {
                // 12 PM to 4:59 PM
                this.afternoonTimeSlots.push(dateObj);
            } else {
                // 5 PM and later
                this.eveningTimeSlots.push(dateObj);
            }
        });
        this.cd.detectChanges();
    }

    getTimeInLocaleString(date: Date) {
        const localeId = this.localeService.getLocale();
        return date.toLocaleTimeString(localeId, { hour: 'numeric', minute: '2-digit' });
    }

    /**
     * Selects the time slot based on the time slot (datetime) that was previously saved in local storage if one exists.
     * @returns Nothing
     */
    private selectTimeSlotFromLocalStorage() {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

        if (!appointmentRequest?.datetime) {
            return;
        }

        if (appointmentRequest?.employee[0].id === '0') {
            this.manipulateDayOrTimeSlot('time-slot-active', 'off', 'time-slot-active');
            return;
        }

        const timeInDay = convertTo12Hour(
            this.selectDateTimeService.getDateString(new Date(appointmentRequest.datetime)).split('T')[1],
            true
        );
        const timeStringToCompare = this.getTimeInLocaleString(new Date(appointmentRequest.datetime));
        const partOfDay = timeInDay?.includes('AM') ? 'ts-morning-' : 'ts-afternoon-';
        const timeSlots = timeInDay && partOfDay === 'ts-morning-' ? this.morningTimeSlots : this.afternoonTimeSlots;
        const partOfDayIndex = timeSlots.findIndex((t) => this.getTimeInLocaleString(t) === timeStringToCompare);

        if (timeInDay) {
            const selectedTimeSlot = this.elementRef.nativeElement.querySelector(`#${partOfDay}${partOfDayIndex}`);
            if (selectedTimeSlot) {
                selectedTimeSlot.classList.add('time-slot-active');
            }
        }
    }

    /**
     * Removes all months except the current selected month and months that are passed in the monthExcludedFromDelete array.
     * @param monthExcludedFromDelete - Month indexes of the months that are to be excluded from the remove operation.
     */
    private removeOutDatedMonthAvailabilities(monthExcludedFromDelete: Month[]) {
        if (!monthExcludedFromDelete.find((m) => m.year === this.selected.getFullYear() && m.month === this.selected.getMonth())) {
            monthExcludedFromDelete.push(new Month(this.selected.getFullYear(), this.selected.getMonth()));
        }
        this.monthAvailabilities = this.monthAvailabilities.filter((monthAvailability) =>
            monthExcludedFromDelete.find((m) => m.year === monthAvailability.year && m.month === monthAvailability.month)
        );
    }

    /**
     * Adds or subtracts months from a passed in date.
     * @param date - A date to which months will be added or subtracted from.
     * @param number - A positive or negative number, depending if months need to be added or subtracted.
     * @returns A new date with the added or subtracted months.
     */
    private adjustMonth(date: Date, number: number): Date {
        const newDate = new Date(date.getTime());
        const currentMonth = newDate.getMonth();
        newDate.setMonth(currentMonth + number);
        return newDate;
    }

    /**
     * Adds or subtracts days from a passed in date.
     * @param date - A date to which days will be added or subtracted from.
     * @param number - A positive or negative number, depending if days need to be added or subtracted.
     * @returns A new date with the added or subtracted days.
     */
    private adjustDays(date: Date, number: number): Date {
        const newDate = new Date(date.getTime());
        newDate.setDate(newDate.getDate() + number);
        return newDate;
    }

    /**
     * Handles the left navigation in the 7 day list.
     * @param firstDate - The current first day in the list (before the movement).
     * @param isWithinCurrentMonth - Function reference for checking if a date is within a month
     * @param scrollAmount - Number of days to scroll (default 7 for backward compatibility)
     */
    private scrollWeekLeft(firstDate: Date, isWithinCurrentMonth: Function, scrollAmount: number = 7) {
        const tempDate = new Date();
        firstDate.setDate(firstDate.getDate() - scrollAmount);
        // If the start date is in the past set the first date in the 7 day display to todays date
        if (this.selectDateTimeService.compareDateWithoutTime(firstDate, tempDate) === -1) {
            firstDate = new Date();
        } else {
            // Else check if the there is need to fetch the previous month from the API
            const tempDatePreviousMonth = this.adjustMonth(firstDate, -1);
            const isPreviousMonthUnavailable =
                !isWithinCurrentMonth(tempDatePreviousMonth) &&
                firstDate.getDate() < 15 &&
                !this.isMonthAvailabilitiesLoaded(tempDatePreviousMonth);
            if (isPreviousMonthUnavailable) {
                const startDate = this.selectDateTimeService.getPreviousMonth(firstDate);
                const isMonthIndexUnavailable = this.monthAvailabilities.findIndex((m) => m.month === firstDate.getMonth() - 1) === -1;
                const isBeforeStartDate = this.selectDateTimeService.compareDateWithoutTimeAndDay(startDate, tempDate) !== -1;
                if (firstDate.getDate() < 8 && isMonthIndexUnavailable && isBeforeStartDate) {
                    this.selectDateTimeService.asyncCallFinishedChevronLeft(false);
                }
                if (
                    (firstDate.getDate() > 8 || !this.shouldDisableLeftChevron || (firstDate.getDate() < 8 && isMonthIndexUnavailable)) &&
                    isMonthIndexUnavailable &&
                    isBeforeStartDate &&
                    !this.callToAvailabilitiesInitiated(startDate, this.selectDateTimeService.getEndOfMonth(startDate))
                ) {
                    const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
                    const isFirstAvailable = appointmentRequest?.employee[0].firstAvailable && this.employeeIdList.length;
                    this.fetchFullMonthAvailabilities(
                        startDate,
                        [new Month(firstDate.getFullYear(), firstDate.getMonth())],
                        false,
                        false,
                        isFirstAvailable ? HorizontalScrollDirection.Left : undefined
                    );
                }
            }
        }
        // Update the 7 day list (don't adjust for selection when scrolling)
        this.updateDaySlots(firstDate, false);
        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
        this.updateMonth(this.dateList[0].date);
    }

    /**
     * Handles the right navigation in the 7 day list.
     * @param firstDate - The current first day in the list (before the movement).
     * @param isWithinCurrentMonth - Function reference for checking if a date is within a month
     * @param scrollAmount - Number of days to scroll (default 7 for backward compatibility)
     */
    private scrollWeekRight(firstDate: Date, isWithinCurrentMonth: Function, scrollAmount: number = 7) {
        firstDate.setDate(firstDate.getDate() + scrollAmount);

        // Check if the there is need to fetch the next month from the API
        const tempDateNextMonth = this.adjustMonth(firstDate, 1);
        if (!isWithinCurrentMonth(tempDateNextMonth) && firstDate.getDate() >= 15 && !this.isMonthAvailabilitiesLoaded(tempDateNextMonth)) {
            if (firstDate.getDate() > 21 && this.monthAvailabilities.findIndex((m) => m.month === firstDate.getMonth() + 1) === -1) {
                this.selectDateTimeService.asyncCallFinishedChevronRight(false);
            }
            const startDate = this.selectDateTimeService.getNextMonth(firstDate);
            if (
                (firstDate.getDate() < 21 ||
                    !this.shouldDisableRightChevron ||
                    (firstDate.getDate() > 21 && this.monthAvailabilities.findIndex((m) => m.month === firstDate.getMonth() + 1) === -1)) &&
                this.monthAvailabilities.findIndex((m) => m.month === firstDate.getMonth() + 1) === -1 &&
                !this.callToAvailabilitiesInitiated(startDate, this.selectDateTimeService.getEndOfMonth(startDate))
            ) {
                const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
                const isFirstAvailable = appointmentRequest?.employee[0].firstAvailable && this.employeeIdList.length;
                this.fetchFullMonthAvailabilities(
                    startDate,
                    [new Month(firstDate.getFullYear(), firstDate.getMonth())],
                    false,
                    false,
                    isFirstAvailable ? HorizontalScrollDirection.Right : undefined
                );
            }
        }
        // Update the 7 day list (don't adjust for selection when scrolling)
        this.updateDaySlots(firstDate, false);
        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
        this.updateMonth(this.dateList[0].date);
    }

    /**
     * Checks to see if the last day of the dateList is after the max advance booking date
     * @returns True - If the right chevron should be disabled; False - If the right chevron shouldn't be disabled.
     */
    private shouldDisableRightChevronMaxAdvance() {
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        if (!siteBookingSettings?.appointmentMaxAdvanceBooking) {
            return false;
        }

        const lastIndex = this.dateList.length - 1;
        const lastDateOfTheWeek = this.dateList[lastIndex]?.date;

        if (!lastDateOfTheWeek) {
            return false;
        }
        const newActiveDate = new Date(new Date().getTime() + this.maxDays * 24 * 60 * 60 * 1000);
        if (this.selectDateTimeService.compareDateWithoutTime(lastDateOfTheWeek, newActiveDate) !== -1) {
            return true;
        }
        return false;
    }

    /**
     * Checks to see if availabilities are loaded for a given month.
     * @param date - A date for whos month the check is performed.
     * @returns True - If the availabilities are loaded; False - If the availabilities are not loaded.
     */
    private isMonthAvailabilitiesLoaded(date: Date): boolean {
        return this.monthAvailabilities.some((m) => m.month === date.getMonth() && m.year === date.getFullYear());
    }

    /**
     * Updates the week list with new days.
     * @param startDate - The first day of the week list.
     * @param adjustForSelection - Whether to adjust the start date to position selected date as third item (default: true)
     */
    private updateDaySlots(startDate: Date, adjustForSelection: boolean = true) {
        let finalStartDate: Date;

        if (adjustForSelection) {
            // Calculate the start date so that the provided date appears as the third item
            // Subtract 2 days from the selected date to make it appear at index 2
            const adjustedStartDate = new Date(startDate);
            adjustedStartDate.setDate(adjustedStartDate.getDate() - 2);

            // Ensure we don't go before today
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (adjustedStartDate < today) {
                // If adjusted start would be in the past, start from today
                finalStartDate = today;
            } else {
                finalStartDate = adjustedStartDate;
            }
        } else {
            // Use the provided date as-is for scrolling operations
            finalStartDate = new Date(startDate);
        }

        this.dateList = new Week(finalStartDate).daysInWeek;
        this.handleAPIAvailabilitiesResponse(this.monthAvailabilities.flatMap((m) => m.availabilities));
    }

    /**
     * Selects a time slot if a datetime that was previously chosen exists in local storage.
     * This handles the case where the user returns to this screen.
     */
    private preselectTimeSlot() {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        if (!appointmentRequest?.datetime) {
            return;
        }

        const timeSlot = new Date(appointmentRequest.datetime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        const timeStringToCompare = this.getTimeInLocaleString(new Date(appointmentRequest.datetime));
        let partOfDay = undefined;
        let foundTimeSlotIndex = undefined;
        if (timeSlot.endsWith('AM')) {
            foundTimeSlotIndex = this.morningTimeSlots.findIndex((mts) => this.getTimeInLocaleString(mts) === timeStringToCompare);
            partOfDay = 'ts-morning-';
        } else {
            foundTimeSlotIndex = this.afternoonTimeSlots.findIndex((ats) => this.getTimeInLocaleString(ats) === timeStringToCompare);
            partOfDay = 'ts-afternoon-';
        }

        // The compareDateWithoutTime check will check if the user is currently on the day that is saved in local storage.
        // Only in this case the time slot is preselected, and if the user navigates to a different day, no time slot is preselected.
        if (
            foundTimeSlotIndex !== -1 &&
            this.selectDateTimeService.compareDateWithoutTime(this.selected, new Date(appointmentRequest.datetime)) === 0
        ) {
            this.manipulateDayOrTimeSlot(`#${partOfDay}${foundTimeSlotIndex}`, 'on', 'time-slot-active');
        }
    }

    /**
     * Selects or deselects a day or time slot.
     * @param slotToManipulate - An indicator if a day or time slot is to be selected/deselected.
     * @param manipulateAction - An indicator if the slotToManipulate needs to be selected or deselected.
     * @param cssClass - A name of an css class that is to be added ir removed in order to selected/deselected the slotToManipulate.
     */
    private manipulateDayOrTimeSlot(slotToManipulate: string, manipulateAction: string, cssClass: string) {
        if (manipulateAction === 'on') {
            const selectedSlot = this.elementRef.nativeElement.querySelector(slotToManipulate);
            selectedSlot?.classList.add(cssClass);
        } else if (manipulateAction === 'off') {
            const previousSelectedSlot = this.elementRef.nativeElement.querySelector(`.${slotToManipulate}`);
            if (previousSelectedSlot) {
                previousSelectedSlot.classList.remove(cssClass);
            }
        }
    }

    /**
     * Updates the hasAvailabilities status of each day in the given dateList based on the montAvailabilities provided.
     * @param dateList An array of DayInWeek objects representing the days within a week.
     * @param montAvailabilities An array of MonthAvailability objects representing the availabilities for each month.
     */
    private updateDaysInWeek(dateList: DayInWeek[], montAvailabilities: MonthAvailability[]): void {
        const applyAvailability = (list: DayInWeek[]) => {
            list.forEach((day) => {
                const { date } = day;
                const monthAvailability = montAvailabilities.find((ma) => ma.month === date.getMonth() && ma.year === date.getFullYear());

                day.hasAvailabilities = monthAvailability
                    ? monthAvailability.availabilities.some((a) => new Date(a.start).getDate() === date.getDate())
                    : false;
            });
        };

        applyAvailability(dateList);
        applyAvailability(this.displayDateList);

        this.cd.detectChanges();
    }

    private async autoSelectFirstAvailableDate(): Promise<void> {
        if (this.dateList.length === 0) {
            return;
        }

        const nextAvailableStartDate = this.getNextAvailableStartDate();
        const currentDate = new Date();
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        const nextAvailableEndDate = this.calculateNextAvailableEndDate(nextAvailableStartDate, currentDate, siteBookingSettings);

        // If nextAvailableEndDate is not later than nextAvailableStartDate, early return.
        if (this.selectDateTimeService.compareDateWithoutTime(nextAvailableEndDate, nextAvailableStartDate) !== 1) {
            return;
        }

        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const availabilities$: Observable<Availability[]>[] = [];
        const availabilitiesCall = new AvailabilitiesCall(nextAvailableStartDate, nextAvailableEndDate);
        this.availabilitiesCalls.push(availabilitiesCall);

        if (appointmentRequest?.employee[0].firstAvailable && this.employeeIdList.length) {
            this.fetchAvailabilitiesForAllEmployees(availabilities$, this.employeeIdList, nextAvailableStartDate, nextAvailableEndDate);
            forkJoin(availabilities$)
                .pipe(
                    takeUntil(this._destroyed$),
                    catchError((error) => {
                        this.logError(error);
                        return of([]);
                    })
                )
                .subscribe((allAvailabilities) => {
                    const flattenedAvailabilities = allAvailabilities.flat();
                    this.handleAvailabilities(flattenedAvailabilities);
                });
        } else {
            this.fetchSingleEmpAvailabilities(
                nextAvailableStartDate,
                nextAvailableEndDate,
                false,
                false,
                appointmentRequest.employee[0].id,
                appointmentRequest.rescheduleGuid
            );
        }
    }

    private getNextAvailableStartDate(): Date {
        const lastDate = this.dateList[this.dateList.length - 1];
        const nextAvailableStartDate = new Date(lastDate.date);
        nextAvailableStartDate.setDate(nextAvailableStartDate.getDate() + 1);
        return this.selectDateTimeService.removeTimeFromDate(new Date(nextAvailableStartDate));
    }

    private calculateNextAvailableEndDate(startDate: Date, currentDate: Date, siteBookingSettings: SiteBookingSettings): Date {
        let nextAvailableEndDate = new Date(startDate);
        nextAvailableEndDate.setMonth(nextAvailableEndDate.getMonth() + 3, 0); // 0 represent last day of the previous month

        if (siteBookingSettings?.appointmentMaxAdvanceBooking) {
            const maxAdvanceDate = new Date(currentDate.getTime() + this.maxDays * 24 * 60 * 60 * 1000);
            if (this.selectDateTimeService.compareDateWithoutTime(maxAdvanceDate, nextAvailableEndDate) === -1) {
                nextAvailableEndDate = maxAdvanceDate;
            }
        }
        return this.selectDateTimeService.removeTimeFromDate(nextAvailableEndDate);
    }

    private handleAvailabilities(availabilities: Availability[]): void {
        if (availabilities.length > 0) {
            const firstAvailableDate = new Date(availabilities[0].start);
            this.handleAvailableDate(firstAvailableDate);
        }
    }

    private handleAvailableDate(date: Date): void {
        this.selected = this.selectDateTimeService.removeTimeFromDate(new Date(date));
        this.updateDaySlots(this.selected);
        this.splitSelectedDateAvailabilityTimes();
        this.manipulateDayOrTimeSlot('time-slot-active', 'off', 'time-slot-active');
        this.manipulateDayOrTimeSlot(`#day-slot-${this.selected.getDate()}-${this.selected.getMonth()}`, 'on', 'day-slot-active');
        this.updateDaysInWeek(this.dateList, this.monthAvailabilities);
        this.shouldDisableRightChevronMaxAdvanceBook = this.shouldDisableRightChevronMaxAdvance();
    }

    private fetchSingleEmpAvailabilities(
        startDate: Date,
        endDate: Date,
        showSpinner: boolean,
        showCalendarSpinner: boolean,
        employeeId: string,
        guidToIgnore?: string
    ): void {
        this.availabilitiesService
            .getAvailabilities(
                this.selectDateTimeService.getDateString(startDate),
                this.selectDateTimeService.getDateString(endDate),
                showSpinner,
                showCalendarSpinner,
                employeeId,
                undefined,
                guidToIgnore
            )
            .pipe(
                takeUntil(this._destroyed$),
                catchError((error) => {
                    this.logError(error);
                    return of([]);
                })
            )
            .subscribe((availabilities) => {
                if (availabilities?.length > 0) {
                    this.populateMonthAvailabilities(availabilities);
                    this.updateNoAvailabilityState();
                }
            });
    }

    private logError(error: any): void {
        console.error('Error occurred while fetching availabilities: ', error);
    }

    private updateMonth(date: Date): void {
        // Don't auto-select dates when navigating - user must explicitly click
        // Just update change detection
        this.cd.detectChanges();
    }

    private async setServiceAndEmployeeToLocalStorage(appointmentRequest: AppointmentRequest) {
        // Service
        if (appointmentRequest.service?.length > 0 && !appointmentRequest.service[0].employeeIDList) {
            const serviceList = await this.serviceService.getServiceList();
            appointmentRequest.service = serviceList.filter((service) => appointmentRequest.service[0].id === service.id);

            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Service, appointmentRequest.service);
            this.localStorageService.setNested(OLD_APPOINTMENT_REQUEST, LSKey.Service, appointmentRequest.service);
        }

        // Employee
        if (appointmentRequest?.employee && appointmentRequest.employee?.length > 0) {
            const employeeId = appointmentRequest.employee[0].id;
            const serviceIds = appointmentRequest.service.map((s) => s.id);

            const employeeList = await this.employeeService.getEmployeeList([employeeId], serviceIds);

            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, employeeList);
            this.localStorageService.setNested(OLD_APPOINTMENT_REQUEST, LSKey.Employee, employeeList);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions = [];
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    private setupMonthNavigation() {
        this.selectDateTimeService.nextMonthClicked.subscribe((val) => {
            if (val) {
                this.handleMonthNavigation(true);
            }
        });

        this.selectDateTimeService.previousMonthClicked.subscribe((val) => {
            if (val) {
                this.handleMonthNavigation(false);
            }
        });
    }

    private handleMonthNavigation(isScrollForward: boolean) {
        let shouldLoad;
        const monthOffset = isScrollForward ? 1 : -1;
        if (isScrollForward) {
            shouldLoad = this.isDateInLastWeek(this.displayDate);
        } else {
            shouldLoad = this.isDateInFirstWeek(this.displayDate);
        }

        if (!shouldLoad) {
            return;
        }

        const date = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth() + monthOffset, 1);
        this.checkAndLoadMonth(date);
    }

    private checkAndLoadMonth(date: Date) {
        const isMonthLoaded = this.monthAvailabilities.some((m) => m.month === date.getMonth() && m.year === date.getFullYear());
        if (!isMonthLoaded) {
            this.isDateWeekLoading = true;
            this.fetchFullMonthAvailabilities(date, [], true, true);
        }
    }

    private isDateInLastWeek(date: Date): boolean {
        const year = date.getFullYear();
        const month = date.getMonth();
        const lastDay = new Date(year, month + 1, 0);
        const start = this.getWeekStart(lastDay);
        const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        return date >= start && date <= last;
    }

    private getWeekStart(date: Date, weekLength = 7): Date {
        const d = new Date(date);
        d.setDate(d.getDate() - (weekLength - 1));
        return d;
    }

    private isDateInFirstWeek(date: Date): boolean {
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth(), 7);
        return date >= start && date <= end;
    }

    private isLeftChevronDisabled(): boolean {
        return this.selectDateTimeService.compareDateWithoutTime(this.firstDay, this.dateList[0].date) !== -1;
    }

    private scrollToSelectedDate() {
        const el = this.datesScroller?.nativeElement as HTMLElement;

        if (!el) {
            return;
        }

        setTimeout(() => {
            const elements = el.querySelectorAll('.date-block');

            const selectedIndex = Array.from(elements).findIndex((btn) => btn.classList.contains('bg-primary-base'));

            if (selectedIndex >= 0) {
                const selectedEl = elements[selectedIndex] as HTMLElement;
                const offset = selectedEl.offsetLeft - 40; // small left margin

                if (typeof el.scrollTo === 'function') {
                    el.scrollTo({ left: offset, behavior: 'smooth' });
                } else {
                    el.scrollLeft = offset;
                }

                this.displayDate = this.selected;
            }
        }, 200);
    }

    onDateBlockHover(date: Date) {
        this.displayDate = date;
    }
}
